// The audio-track reconciliation state machine (CPACWEB-420).
//
// Plain TypeScript, no React: the machine holds one piece of intent — the desired audio
// language, owned by Video.tsx and read through deps.getDesiredLang() — and keeps comparing
// it against the player's actual track on every meaningful player event, re-asserting until
// the player agrees. The loop, not any single command, is the correctness guarantee: on
// iPhone, AVFoundation auto-selects audio by device locale (AUTOSELECT=YES on every Vualto
// rendition) and can re-assert after seeks or backgrounding, and native switches are async.
//
// All side effects go through injected dependencies (player access, clock, timers,
// callbacks), so every behaviour is unit-testable offline with a fake player — see
// audio-track-reconciler.test.ts. CpacJwPlayerContainer.tsx adapts JW events into this machine.
//
// Architecture doc: doc/video-player-audio-architecture.md

import {
  AudioLang,
  audioDebug,
  chooseTrackIndex,
  classifyUncommandedChange,
  PlayerAudioTrack,
  playerTrackLang,
} from "./audio-tracks.ts";

/** The player surface the machine needs — a read/command interface, nothing more. */
export type ReconcilerPlayer = {
  getAudioTracks: () => PlayerAudioTrack[] | undefined;
  getCurrentAudioTrack: () => number;
  setCurrentAudioTrack: (index: number) => void;
};

/**
 * Player events that open the turbulence window: the platform may (re)run its own audio
 * auto-selection around any of these, so an uncommanded track change shortly after one is
 * treated as the platform fighting us, not the user choosing.
 */
export type TurbulenceTrigger =
  | "ready"
  | "play"
  | "resume"
  | "tracks-populated"
  | "first-frame"
  | "seeked"
  | "foreground";

/**
 * Every reason a reconcile pass can run. Superset of TurbulenceTrigger: the three internal
 * reasons deliberately BYPASS the turbulence stamp — our own activity must not widen the
 * window in which a user's gear-menu pick would be misclassified as a platform re-assertion.
 * The reason has no behavioural effect inside reconcile(); it is the label carried into the
 * always-on command / GAVE UP log lines and the debug trace.
 */
export type ReconcileReason =
  | TurbulenceTrigger
  | "desired-lang-change"
  | "recheck"
  | "platform-reassertion";

export type ReconcilerDeps = {
  getPlayer: () => ReconcilerPlayer | null;
  /** The desired language — owned by Video.tsx; the machine never stores its own copy. */
  getDesiredLang: () => AudioLang;
  /** The confirmed active track's language changed (drives the buttons' selected state). */
  onActiveLang: (lang: AudioLang | undefined) => void;
  /** The user picked a track themselves (JW gear menu) — parent should adopt it as desired. */
  onAdoptLang: (lang: AudioLang) => void;
  /** Injectable for tests; default Date.now / setTimeout / clearTimeout. */
  now?: () => number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (handle: unknown) => void;
};

// Circuit-breaker tuning. MAX_ATTEMPTS bounds unconfirmed commands per breaker cycle; the
// cooldown is how long after the last command the tripped breaker may re-arm. Together they
// make a hot loop impossible while guaranteeing the loop always restarts on later real events
// — the breaker exists for genuine deadlocks (a cast receiver ignoring JS, a track the
// player refuses); tripping is never a permanent surrender.
export const MAX_ATTEMPTS = 3;
export const BREAKER_COOLDOWN_MS = 5000;
// Every command is followed by one delayed re-read: JW does not reliably emit
// audioTrackChanged (not for its own initial selection; native iOS switches are async).
// A confirmed reconcile schedules nothing, so the chain terminates naturally.
export const RECHECK_DELAY_MS = 1000;

export class AudioTrackReconciler {
  private readonly now: () => number;
  private readonly setTimer: (fn: () => void, ms: number) => unknown;
  private readonly clearTimer: (handle: unknown) => void;

  private attempts = 0;
  private exhaustedWarned = false;
  private commandedIdx: number | null = null;
  private lastCommandAt: number | undefined;
  private lastTurbulenceAt: number | undefined;
  private giveUps = 0;
  private lastReportedLang: AudioLang | undefined;
  private recheckTimer: unknown | null = null;
  private disposed = false;

  constructor(private readonly deps: ReconcilerDeps) {
    this.now = deps.now ?? (() => Date.now());
    this.setTimer = deps.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
    this.clearTimer = deps.clearTimer ?? ((handle) => clearTimeout(handle as ReturnType<typeof setTimeout>));
  }

  /** Cumulative breaker trips (one per exhausted cycle) — QA reports this number. */
  get totalGiveUps(): number {
    return this.giveUps;
  }

  /** A player event the platform may auto-select around: stamp turbulence and reconcile. */
  handleTrigger(trigger: TurbulenceTrigger): void {
    this.lastTurbulenceAt = this.now();
    this.reconcile(trigger);
  }

  /** The parent's desired language changed (button click or adopted gear pick). */
  desiredLangChanged(): void {
    this.commandedIdx = null;
    this.rearmBreaker();
    this.reconcile("desired-lang-change");
  }

  /** The stream source was replaced (prelive→live swap, live rendition reload). */
  sourceChanged(): void {
    this.commandedIdx = null;
    this.rearmBreaker();
    this.reportActiveLang(undefined);
  }

  /**
   * The player reports a track change. The event payload index is authoritative — the
   * read-back can still report the OLD track while the switch is in flight (observed on
   * desktop, guaranteed on native iOS) — so the adapter passes event.currentTrack here.
   */
  handleTrackChanged(eventCurrentTrack?: number): void {
    const player = this.deps.getPlayer();
    if (!player) return;
    const tracks = player.getAudioTracks();
    const current = eventCurrentTrack ?? player.getCurrentAudioTrack();
    if (!tracks || current == null || current < 0 || !tracks[current]) return;
    const currentLang = playerTrackLang(tracks[current]);
    if (this.commandedIdx === current) {
      // Our own command, confirmed by the player.
      this.commandedIdx = null;
      this.rearmBreaker();
      this.reportActiveLang(currentLang);
      audioDebug("audioTrackChanged: command confirmed", { current, currentLang });
      return;
    }
    // A change we didn't command: near a seek/foreground/startup it's the platform
    // re-asserting its own auto-selection (fight it); in steady playback it's the user
    // picking a track in JW's gear menu (adopt it — the buttons follow).
    const verdict = classifyUncommandedChange(
      this.now(),
      Math.max(this.lastTurbulenceAt ?? 0, this.lastCommandAt ?? 0) || undefined,
    );
    if (verdict === "user") {
      this.rearmBreaker();
      this.reportActiveLang(currentLang);
      // Flow the choice UP: Video.tsx owns the desired language; never override it here.
      this.deps.onAdoptLang(currentLang);
      audioDebug("audioTrackChanged: adopted user choice", { currentLang });
    } else {
      audioDebug("audioTrackChanged: platform re-assertion", { currentLang, desired: this.deps.getDesiredLang() });
      this.reconcile("platform-reassertion");
      // The immediate reconcile can read mid-switch state and falsely confirm; the delayed
      // re-check sees where the player actually settled.
      this.scheduleRecheck();
    }
  }

  /** Cancel pending timers; the machine issues nothing after this. */
  dispose(): void {
    this.disposed = true;
    if (this.recheckTimer !== null) {
      this.clearTimer(this.recheckTimer);
      this.recheckTimer = null;
    }
  }

  // ---- internals ----

  private reconcile(trigger: ReconcileReason): void {
    if (this.disposed) return;
    const player = this.deps.getPlayer();
    if (!player) return;
    const tracks = player.getAudioTracks();
    if (!tracks || tracks.length === 0) {
      audioDebug("reconcile: no tracks yet", { trigger });
      return;
    }
    const desired = this.deps.getDesiredLang();
    const idx = chooseTrackIndex(desired, tracks);
    if (idx === undefined) {
      audioDebug("reconcile: no candidate track", { trigger, desired, tracks });
      return;
    }
    const current = player.getCurrentAudioTrack();
    if (current === idx) {
      this.rearmBreaker();
      this.commandedIdx = null;
      this.reportActiveLang(playerTrackLang(tracks[idx]));
      audioDebug("reconcile: confirmed", { trigger, desired, idx });
      return;
    }
    if (current < 0) {
      // PRESET phase: the player knows its tracks but has no active one yet (preload
      // "none", before first frame). A switch cannot be verified here, so command once as
      // a preset WITHOUT burning breaker attempts, and re-check shortly after.
      if (this.commandedIdx !== idx) {
        this.commandedIdx = idx;
        audioDebug("reconcile: preset before playback", { trigger, desired, idx });
        player.setCurrentAudioTrack(idx);
      }
      this.scheduleRecheck();
      return;
    }
    // Re-arm the circuit breaker. If it is tripped (all MAX_ATTEMPTS commands of this cycle
    // issued, none confirmed) AND the cooldown has elapsed since our last command, clear the
    // trip so this pass may command again. This is what makes giving up temporary: every
    // reconcile pass checks it, so the next event after a quiet spell always starts a fresh
    // cycle. Gating on time-since-last-COMMAND (not merely "a new event arrived") is the
    // rate limiter — during a genuine deadlock (e.g. a cast receiver ignoring JS) the
    // machine fires at most MAX_ATTEMPTS commands per cooldown period, one warning per
    // cycle, instead of fighting hot on every event. Note: confirmations and
    // desired-language changes re-arm immediately via rearmBreaker() elsewhere — this
    // time-based valve only matters when the player kept refusing us.
    if (this.attempts >= MAX_ATTEMPTS && this.now() - (this.lastCommandAt ?? 0) >= BREAKER_COOLDOWN_MS) {
      this.rearmBreaker();
    }
    if (this.attempts >= MAX_ATTEMPTS) {
      if (!this.exhaustedWarned) {
        this.exhaustedWarned = true;
        this.giveUps++;
        console.warn(
          `[CPACWEB-420] audio reconcile GAVE UP after ${MAX_ATTEMPTS} attempts: ` +
          `wanted ${desired}, player reports ${this.currentLangOf(tracks, current)} ` +
          `(trigger=${trigger}, totalGiveUps=${this.giveUps})`,
          tracks,
        );
      }
      return;
    }
    this.attempts++;
    this.commandedIdx = idx;
    this.lastCommandAt = this.now();
    // Always-on: fires only when a switch is actually issued — the direct measure of how
    // often we fight the platform's auto-selection.
    console.info(
      `[CPACWEB-420] audio reconcile: ${this.currentLangOf(tracks, current)} → ${desired} ` +
      `(trigger=${trigger}, attempt=${this.attempts}/${MAX_ATTEMPTS})`,
    );
    player.setCurrentAudioTrack(idx);
    this.scheduleRecheck();
  }

  private scheduleRecheck(): void {
    if (this.disposed) return;
    if (this.recheckTimer !== null) this.clearTimer(this.recheckTimer);
    this.recheckTimer = this.setTimer(() => {
      this.recheckTimer = null;
      this.reconcile("recheck");
    }, RECHECK_DELAY_MS);
  }

  private rearmBreaker(): void {
    this.attempts = 0;
    this.exhaustedWarned = false;
  }

  private reportActiveLang(lang: AudioLang | undefined): void {
    if (this.lastReportedLang === lang) return;
    this.lastReportedLang = lang;
    this.deps.onActiveLang(lang);
  }

  private currentLangOf(tracks: PlayerAudioTrack[], idx: number): string {
    return idx >= 0 && tracks[idx] ? playerTrackLang(tracks[idx]) : "none";
  }
}
